import {
    DeleteRounded,
    EditRounded,
    ThumbDownAltRounded,
    ThumbUpAltRounded,
} from "@mui/icons-material";
import { Divider, Grid, IconButton, Theme, Typography } from "@mui/material";
import { SxProps } from "@mui/system";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxMenu, { AcxMenuItemProps } from "components/UI/Menu/AcxMenu";
import moment from "moment";
import React from "react";
import { AcxLLMReasoningFeedbackProps } from "./types";

const DEFAULT_MAX_NOTE_LENGTH = 500;

const NOTE_VISIBILITY_HINT =
    "Notes are only visible to you and Authenticx model reviewers.";

/**
 * Agree / disagree plus an optional note on a single model result's reasoning.
 *
 * Rendered by AcxLLMReasoning beneath the reasoning body, so it appears on every
 * surface that shows the panel. Fully controlled: which of the four visual states
 * shows is derived from `state`, never from internal state.
 *
 *   status "idle"      + agreed null    -> prompt (no opinion yet)
 *   status "idle"      + agreed set     -> composer (selection made, note optional)
 *   status "saving"                     -> composer, submission in flight
 *   status "submitted"                  -> read-only summary, editable
 */
export function AcxLLMReasoningFeedback(props: AcxLLMReasoningFeedbackProps) {
    const { state } = props;

    if (state.status === "submitted") {
        return <FeedbackSummary {...props} />;
    }

    if (state.status === "idle" && state.agreed === null) {
        return <FeedbackPrompt {...props} />;
    }

    return <FeedbackComposer {...props} />;
}

function FeedbackPrompt(props: AcxLLMReasoningFeedbackProps) {
    return (
        <Grid
            container
            item
            justifyContent="space-between"
            alignItems="center"
            columnGap={2}
            rowGap={1}
        >
            <Grid item>
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.secondary[800],
                        fontSize: "12.5px",
                        fontWeight: 600,
                        lineHeight: "20px",
                    })}
                >
                    Do you agree with this reasoning?
                </Typography>
            </Grid>
            <Grid container item width="fit-content" columnGap={1}>
                <VoteButton
                    vote="agree"
                    selected={false}
                    disabled={props.disabled}
                    onClick={props.onAgree}
                />
                <VoteButton
                    vote="disagree"
                    selected={false}
                    disabled={props.disabled}
                    onClick={props.onDisagree}
                />
            </Grid>
        </Grid>
    );
}

function FeedbackComposer(props: AcxLLMReasoningFeedbackProps) {
    const {
        state,
        maxNoteLength = DEFAULT_MAX_NOTE_LENGTH,
        disabled,
        onCancel,
    } = props;

    const note = state.note ?? "";
    const isSaving = state.status === "saving";
    const isAgreed = state.agreed === true;
    // A previous submission is what makes this an update rather than a first save.
    const isUpdate = !!state.submittedAt;
    const controlsDisabled = disabled || isSaving;

    return (
        <Grid container item direction="column" rowGap={1}>
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                columnGap={2}
                rowGap={1}
            >
                <VoteHeadline agreed={isAgreed} />
                <Grid container item width="fit-content" columnGap={1}>
                    <VoteButton
                        vote="agree"
                        selected={isAgreed}
                        disabled={controlsDisabled}
                        onClick={props.onAgree}
                    />
                    <VoteButton
                        vote="disagree"
                        selected={state.agreed === false}
                        disabled={controlsDisabled}
                        onClick={props.onDisagree}
                    />
                </Grid>
            </Grid>
            <Grid container item direction="column">
                <Grid item>
                    <AcxMainTextField
                        id="llm-reasoning-feedback-note"
                        labelText={
                            isAgreed
                                ? "Add a note (optional)"
                                : "Help us understand (optional)"
                        }
                        placeholderText={
                            isAgreed
                                ? "Anything that made this reasoning useful?"
                                : "What did the model get wrong?"
                        }
                        value={note}
                        onChange={(event) =>
                            props.onNoteChange(event.target.value)
                        }
                        isDisabled={controlsDisabled}
                        maxLength={maxNoteLength}
                        multiline
                        rows={3}
                        autoComplete="off"
                        textContainerSx={{ margin: 0 }}
                    />
                </Grid>
                <Grid
                    container
                    item
                    justifyContent="space-between"
                    alignItems="center"
                    columnGap={2}
                >
                    <Grid item>
                        <Typography sx={captionSx}>
                            {NOTE_VISIBILITY_HINT}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography sx={captionSx}>
                            {note.length}/{maxNoteLength}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                item
                justifyContent="flex-end"
                alignItems="center"
                columnGap={1}
            >
                {onCancel && (
                    <AcxButton
                        fullWidth={false}
                        variant="text"
                        color="primary"
                        size="small"
                        disabled={controlsDisabled}
                        disabledTooltip={isSaving ? "Saving..." : "Disabled"}
                        onClick={onCancel}
                        sx={actionButtonSx}
                    >
                        Cancel
                    </AcxButton>
                )}
                <AcxButton
                    fullWidth={false}
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={isSaving}
                    disabled={disabled}
                    disabledTooltip={isSaving ? "Saving..." : "Disabled"}
                    onClick={props.onSubmit}
                    sx={actionButtonSx}
                >
                    {isUpdate ? "Update" : "Save"}
                </AcxButton>
            </Grid>
        </Grid>
    );
}

function FeedbackSummary(props: AcxLLMReasoningFeedbackProps) {
    const { state, disabled, onDelete } = props;
    const isAgreed = state.agreed === true;
    const note = state.note?.trim();

    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(
        null,
    );

    const menuItems: AcxMenuItemProps[] = [
        {
            id: "llm-reasoning-feedback-edit",
            label: "Edit",
            icon: <EditRounded fontSize="small" sx={menuIconSx} />,
            props: {
                onClick: () => {
                    setMenuAnchor(null);
                    props.onEdit();
                },
            },
        },
    ];

    if (onDelete) {
        menuItems.push({
            id: "llm-reasoning-feedback-delete",
            label: "Delete",
            icon: <DeleteRounded fontSize="small" sx={menuIconSx} />,
            props: {
                onClick: () => {
                    setMenuAnchor(null);
                    onDelete();
                },
            },
        });
    }

    return (
        <Grid
            container
            item
            direction="column"
            rowGap={1 / 2}
            sx={{
                // The action is revealed on hover, but must stay reachable by
                // keyboard -- an opacity-0 button is still focusable.
                "&:hover .llm-reasoning-feedback-actions, &:focus-within .llm-reasoning-feedback-actions":
                    { opacity: 1 },
            }}
        >
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                columnGap={2}
                flexWrap="nowrap"
            >
                <VoteHeadline agreed={isAgreed} />
                <Grid item>
                    <IconButton
                        className="llm-reasoning-feedback-actions"
                        size="small"
                        aria-label="Edit feedback"
                        title="Edit feedback"
                        disabled={disabled}
                        onClick={(event) => setMenuAnchor(event.currentTarget)}
                        sx={(theme) => ({
                            opacity: menuAnchor ? 1 : 0,
                            transition: "opacity 120ms ease",
                            color: theme.palette.gray.main,
                            ":hover": { color: theme.palette.gray[700] },
                        })}
                    >
                        <EditRounded fontSize="small" />
                    </IconButton>
                    <AcxMenu
                        anchorElement={menuAnchor}
                        menuItems={menuItems}
                        onMenuClose={() => setMenuAnchor(null)}
                    />
                </Grid>
            </Grid>
            {note && (
                <React.Fragment>
                    <Grid item>
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.gray[700],
                                fontSize: "12.5px",
                                lineHeight: "20px",
                            })}
                        >
                            {note}
                        </Typography>
                    </Grid>
                    {state.submittedAt && (
                        <Grid item>
                            <Typography sx={captionSx}>
                                {moment(state.submittedAt).format("MM/DD/YYYY")}
                            </Typography>
                        </Grid>
                    )}
                </React.Fragment>
            )}
        </Grid>
    );
}

function VoteHeadline(props: { agreed: boolean }) {
    const Icon = props.agreed ? ThumbUpAltRounded : ThumbDownAltRounded;

    return (
        <Grid
            container
            item
            width="fit-content"
            alignItems="center"
            columnGap={1}
            flexWrap="nowrap"
        >
            <Grid item display="flex">
                <Icon
                    fontSize="small"
                    sx={(theme) => ({
                        fontSize: "14px",
                        color: props.agreed
                            ? theme.palette.secondary[800]
                            : theme.palette.error[900],
                    })}
                />
            </Grid>
            <Grid item>
                <Typography
                    sx={(theme) => ({
                        color: props.agreed
                            ? theme.palette.secondary[800]
                            : theme.palette.error[900],
                        fontSize: "12.5px",
                        fontWeight: 700,
                        lineHeight: "20px",
                    })}
                >
                    You {props.agreed ? "agree" : "disagree"} with this result
                </Typography>
            </Grid>
        </Grid>
    );
}

function VoteButton(props: {
    vote: "agree" | "disagree";
    selected: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    const isAgree = props.vote === "agree";
    const Icon = isAgree ? ThumbUpAltRounded : ThumbDownAltRounded;

    return (
        <Grid item>
            <AcxButton
                fullWidth={false}
                variant="outlined"
                size="small"
                disabled={props.disabled}
                onClick={props.onClick}
                startIcon={<Icon sx={{ fontSize: "13px" }} />}
                sx={(theme) => {
                    const accent = isAgree
                        ? theme.palette.secondary[800]
                        : theme.palette.error[900];
                    const tint = isAgree
                        ? theme.palette.secondary[50]
                        : theme.palette.error[50];

                    return {
                        // AcxButton disallows a "secondary" color, so the agree
                        // and disagree treatments are both driven through sx.
                        height: "32px",
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: "20px",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        color: props.selected ? accent : theme.palette.gray.main,
                        borderColor: props.selected
                            ? accent
                            : theme.palette.gray[200],
                        backgroundColor: props.selected ? tint : "transparent",
                        ":hover": {
                            color: accent,
                            borderColor: accent,
                            backgroundColor: tint,
                        },
                    };
                }}
            >
                {isAgree ? "Agree" : "Disagree"}
            </AcxButton>
        </Grid>
    );
}

const menuIconSx: SxProps<Theme> = (theme) => ({
    color: theme.palette.gray.main,
});

const captionSx: SxProps<Theme> = (theme) => ({
    color: theme.palette.gray[400],
    fontSize: "11px",
    lineHeight: "20px",
});

const actionButtonSx: SxProps<Theme> = {
    height: "32px",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "20px",
    textTransform: "none",
};

/**
 * The divider AcxLLMReasoning draws between the reasoning body and the feedback
 * controls. Exported so the panel keeps its own layout in one place.
 */
export function AcxLLMReasoningFeedbackDivider() {
    return (
        <Grid item>
            <Divider
                sx={(theme) => ({ borderColor: theme.palette.gray[200] })}
            />
        </Grid>
    );
}
